import { useState } from 'react'

import { Menu } from '@mantine/core'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import { ApplicantStage, HiringStage } from 'src/graphql/types/applicants'
import IconChevronDown from 'src/icons/IconChevronDown'
import { calculateStageBadgeColor, formatSnakeValue } from 'src/lib/formatters'

import DisqualifiedModal from './StageModals/DisqualifiedModal/DisqualifiedModal'
import HiredModal from './StageModals/HiredModal/HiredModal'
import NotInterestedModal from './StageModals/NotInterestedModal/NotInterestedModal'
import ReenterProspectModal from './StageModals/ReenterProspectModal/ReenterProspectModal'
import TerminatedModal from './StageModals/TerminatedModal/TerminatedModal'

const ApplicantStageMenu = ({ applicant, handleStageChange }) => {
  const [ModalComponent, setModalComponent] = useState(null)

  const handleValueChange = (value) => {
    const modalMap = {
      [ApplicantStage.NOT_INTERESTED]: NotInterestedModal,
      [ApplicantStage.DISQUALIFIED]: DisqualifiedModal,
      [ApplicantStage.TERMINATED]: TerminatedModal,
      [ApplicantStage.HIRED]: HiredModal,
    }

    const Modal = modalMap[value]
    if (Modal) {
      setModalComponent(() => () => (
        <Modal
          onSave={(values) =>
            handleStageChange({ applicantStage: value, ...values })
          }
          onClose={() => setModalComponent(null)}
          applicant={applicant}
        />
      ))
    } else {
      handleStageChange({
        applicantStage: value,
        applicantStageDate: new Date(),
      })
    }
  }

  return (
    <>
      <div className="flex w-full flex-row items-center justify-between gap-2 text-xs font-medium capitalize text-doubleNickel-gray-500">
        <Menu shadow="md" position="bottom-start">
          <Menu.Target>
            {/* Wrapper div forwards the ref Menu.Target needs (StyledBadge does
                not). It is focusable and opens on Enter/Space so keyboard users
                keep what the old chevron button gave them. The badge is colored
                by stage: Hired green, Terminated red (ENG-4946; the shared
                helper also used by search/merge). */}
            <div
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.currentTarget.click()
                }
              }}
            >
              <StyledBadge
                clickable
                textTransform="capitalize"
                color={calculateStageBadgeColor(applicant.applicantStage)}
                rightSection={<IconChevronDown className="h-2.5 w-2.5" />}
              >
                {applicant.applicantStage === ApplicantStage.PROSPECT
                  ? formatSnakeValue(
                      applicant.hiringStage ?? ApplicantStage.PROSPECT
                    )
                  : formatSnakeValue(applicant.applicantStage)}
              </StyledBadge>
            </div>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Label className="text-sm font-semibold text-doubleNickel-gray-800">
              Prospect
            </Menu.Label>
            {Object.values(HiringStage).map((stage, index) => (
              <Menu.Item
                className="pl-6 text-xs font-medium text-doubleNickel-gray-600"
                key={index}
                onClick={() => {
                  if (
                    applicant.applicantStage !== ApplicantStage.PROSPECT
                  ) {
                    setModalComponent(() => () => (
                      <ReenterProspectModal
                        hiringStage={stage}
                        onSave={(values) => handleStageChange(values)}
                        onClose={() => setModalComponent(null)}
                      />
                    ))
                  } else {
                    handleStageChange({
                      hiringStage: stage,
                      applicantStage: 'PROSPECT',
                    })
                  }
                }}
              >
                {formatSnakeValue(stage)}
              </Menu.Item>
            ))}
            {Object.values(ApplicantStage)
              .filter((stage) => stage !== 'PROSPECT')
              .map((stage, index) => (
                <Menu.Item
                  key={index}
                  onClick={() => handleValueChange(stage)}
                  className="text-sm font-semibold text-doubleNickel-gray-800"
                >
                  {formatSnakeValue(stage)}
                </Menu.Item>
              ))}
          </Menu.Dropdown>
        </Menu>
        <div className="ml-auto text-xs font-medium text-doubleNickel-gray-500">
          {applicant.applicantStageDate &&
            `${dayjs.utc(applicant.applicantStageDate).format('MM/DD/YYYY')}`}
        </div>
      </div>
      {ModalComponent && ModalComponent()}
    </>
  )
}

export default ApplicantStageMenu
